/**
 * Pure state machine for the inline VIES (VAT Information Exchange System) check shown next to
 * the UST-ID field in both the signup selector (Personinfo) and the account editor's reuse of
 * `BusinessVatFields`. Kept separate from any component so a form's "can I proceed" gate uses
 * EXACTLY the same decision the field uses to render its own feedback — no second copy of the
 * blocking rule to drift out of sync.
 *
 * The UST-ID field has five visible states; `empty` never reaches this function (an empty value
 * is governed by the field's own `required` validation, not by a VIES result).
 *
 * The VIES result is INFORMATIONAL and never blocks the form: the member can always proceed, and
 * the BACKEND re-runs VIES authoritatively at order time to decide reverse-charge vs. charging VAT.
 * A number VIES can't confirm (invalid / unavailable / not-yet-checked) simply means "no confirmed
 * reverse charge" → the member is charged VAT, exactly as a private customer, and is never stuck.
 *   - checking:    `inFlight` — spinner / "Validating…" (does not block)
 *   - valid:       VIES confirmed — shows the registered company name
 *   - invalid:     VIES rejected — shows the error, but the member may still proceed (pays VAT)
 *   - unavailable: VIES unreachable — shows the taxable note
 *   - idle:        no check completed for the current value — no message
 */

export type VatCheckStatus = 'idle' | 'valid' | 'invalid' | 'unavailable'

export type VatFieldVariant = 'idle' | 'checking' | 'success' | 'error' | 'warning'

export interface VatFieldUiState {
  /** true = a form using this field must not be allowed to proceed while this is the state. */
  blocks: boolean
  variant: VatFieldVariant
  /** i18n key for the message to show under the field, or null to show nothing. */
  messageKey: string | null
}

export function vatFieldState(status: VatCheckStatus, inFlight: boolean): VatFieldUiState {
  // No VIES state ever blocks — the backend decides reverse-charge vs. VAT at order time, so the
  // member is never stuck on a flaky/failed check. `blocks` stays in the shape for callers that
  // still read it, always false.
  if (inFlight) {
    return {blocks: false, variant: 'checking', messageKey: 'VAT_VIES_CHECKING'}
  }

  switch (status) {
    case 'valid':
      return {blocks: false, variant: 'success', messageKey: 'VAT_VIES_VALID'}
    case 'invalid':
      return {blocks: false, variant: 'error', messageKey: 'VAT_VIES_INVALID'}
    case 'unavailable':
      return {blocks: false, variant: 'warning', messageKey: 'VAT_VIES_UNAVAILABLE'}
    case 'idle':
    default:
      return {blocks: false, variant: 'idle', messageKey: null}
  }
}
